import { StatusCode } from "api/protocols";
import { GetLoyaltyProgramRewards, GetLoyaltyProgramTiers } from "api/rpc/2024-04/facilityAdmin/customer/loyalty";
import { GetLoyaltyCustomer, GetLoyaltyCustomerExpiringPoints, PostCustomerLoyaltyProgram, PostCustomerLoyaltyProgramTransaction } from "api/rpc/2024-04/facilityAdmin/customer/loyaltyCustomer";
import axios, { CancelToken } from "axios";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input";
import MessageDisplay from "components/messageDisplay/MessageDisplay";
import { NavigationDropdownNew } from "components/navigationDropdownNew/NavigationDropdownNew";
import Sheet from "components/sheet/Sheet";
import Spin from "components/spin/spin";
import Portal from "elements/Portal";
import useModal from "hooks/modals/useModal";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import PortalLoyaltyProgramView from "pages/guest/Portal/portalLoyaltyProgram/PortalLoyaltyProgramView";
import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { showError, showSuccess } from "redux/actions/ui";
import { ILoyaltyCustomer, IReward, ITier } from "redux/reducers/models/loyalty";


export default function CustomerLoyaltyProgram(props: {customerId: string}) {
    const dispatch = useAppDispatch();
    const {facilityStore, authStore} = useAppSelector(store => store);
    const {t} = useTranslation();
    const [customer, setCustomer] = useState<ILoyaltyCustomer>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [tiers, setTiers] = useState<Array<ITier>>([]);
    const [rewards, setRewards] = useState<Array<IReward>>([]);
    const [expiringPoints, setExpiringPoints] = useState<number>(0);
    const { state: pointsAdjustModal, updateModal: updatePointsAdjustModal, closeModal: closePointsAdjustModal } = useModal({amount: ""});

    useEffect(() => {
        const source = axios.CancelToken.source();
        void getCustomer(source.token);
        return () => source.cancel();
    }, []);

    useEffect(() => {
        const source = axios.CancelToken.source();
        if (customer) {
            void getProgram(source.token);
        }
        return () => source.cancel();
    }, [customer]);

    async function getCustomer(cancelToken?: CancelToken) {
        setLoading(true);
        const res = await GetLoyaltyCustomer({customer_id: Number(props?.customerId), offset: 0}, false, cancelToken);
        // setLoading(false);
        if (res?.status !== StatusCode.OK){
            if (cancelToken && cancelToken?.reason) {
                return;
            }
            setCustomer(undefined);
            dispatch(showError(t("Error getting loyalty customer")));
            return;
        }
        if (res?.data?.length > 0) {
            const expiringRes = await GetLoyaltyCustomerExpiringPoints({customer_id: Number(props?.customerId)}, false, cancelToken);
            if (expiringRes?.status !== StatusCode.OK) {
                if (cancelToken && cancelToken?.reason) {
                    return;
                }
                setLoading(false);
                dispatch(showError(t("Error getting expiry points")));
            }
            ReactDOM.unstable_batchedUpdates(() => {
                setExpiringPoints(expiringRes?.status === StatusCode.OK ? expiringRes?.data : 0);
                setCustomer(res?.data[0]);
            });
        } else {
            ReactDOM.unstable_batchedUpdates(() => {
                setCustomer(undefined);
                setLoading(false);
            });
            
        }
    }

    async function getProgram(cancelToken?: CancelToken) {
        let tiers: Array<ITier> = [];
        setLoading(true);
        const rewardsRes = await GetLoyaltyProgramRewards(false, cancelToken);
        if (rewardsRes?.status !== StatusCode.OK){
            if (cancelToken && cancelToken?.reason) {
                return;
            }
            setLoading(false);
            dispatch(showError(t("Error getting loyalty rewards")));
            return;
        }
        if (facilityStore?.loyaltyProgram?.type === "tier_based") {
            const tierRes = await GetLoyaltyProgramTiers(false, cancelToken);
            if (tierRes?.status !== StatusCode.OK) {
                if (cancelToken && cancelToken?.reason) {
                    return;
                }
                setLoading(false);
                dispatch(showError(t("Error getting loyalty tiers")));
                return;
            }
            tiers = tierRes?.data;
        }
        ReactDOM.unstable_batchedUpdates(() => {
            setLoading(false);
            setRewards(rewardsRes?.data);
            setTiers(tiers);
        });
    }
 
    async function joinLoyaltyProgram() {
        const res = await PostCustomerLoyaltyProgram({customer_id: Number(props?.customerId)}, true);
        if (res?.status !== StatusCode.OK) {
            dispatch(showError(t("Error joining loyalty program")));
            return;
        }
        dispatch(showSuccess(t("Successfully joined loyalty program")));
        await getCustomer();
    }
    
    async function pointsAdjustment() {
        const res = await PostCustomerLoyaltyProgramTransaction({customer_id: Number(props?.customerId), points_adjustment: Math.round(Number(pointsAdjustModal?.amount))}, true);
        if (res?.status !== StatusCode.OK) {
            dispatch(showError(t("Error making points adjustment")));
            return;
        }
        void closePointsAdjustModal();
        dispatch(showSuccess(t("Successfully created points adjustment")));
        await getCustomer();
    }

    return (
        loading ? 
        (<div style={{display: "flex", width: "40px", margin: "auto"}}><Spin/></div> ) : customer && rewards ?
            <>
            <div className="w-full flex items-center justify-between">
                <h1>{t("Loyalty Program")}</h1>
                <NavigationDropdownNew
                  label={t("Options")}
                  rightAlign
                  sections={[
                    [
                      {
                        icon: "plus",
                        label: t("Points Adjustment"),
                        onClick: () => updatePointsAdjustModal({isOpen: true}),
                        disabled: !authStore?.user?.permissions?.loyalty_program_points_adjustment,
                      },
                    ],
                  ]}
                />
              </div>
              <br />
            <PortalLoyaltyProgramView loyaltyCustomer={customer} expiringPoints={expiringPoints} tiers={tiers} rewards={rewards} loyaltyProgram={facilityStore?.loyaltyProgram} /> 

            <Portal
            isMounted={pointsAdjustModal.isOpen}
            >
                <Sheet
                size="small"
                open={pointsAdjustModal.isOpen}
                title={t("Points Adjustment")}
                supportText={t("Manually adjust points")}
                okText={Number(pointsAdjustModal?.amount) >= 0 ? t("Add") : t("Subtract")}
                onCancel={closePointsAdjustModal}
                onOk={pointsAdjustment}
                okDisabled={!pointsAdjustModal?.amount}
                >
                    <FormLayout>
                        <FormLayout.Group>
                            <Input label={t("Points to Adjust")} onChange={e => updatePointsAdjustModal({amount: e.target.value})} value={pointsAdjustModal?.amount} type="number" placeholder={"0"} suffix={t("pts")}/>
                        </FormLayout.Group>
                    </FormLayout>
                </Sheet>
            </Portal>
            </>
        : customer === undefined ?
        (
        <div>
        <br />
            <MessageDisplay 
            actions={[{label: t("Join"), action: () => joinLoyaltyProgram()}]}
            icon="gift" 
            title={t("Join Loyalty Program")} 
            content={t("Earn points and get access to exclusive discounts")}
            />
        </div>) : null
    );
}